import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, ShieldAlert, TriangleAlert } from 'lucide-react';

import { useAdminUserDetailQuery } from '@/client/api/admin/users.query';
import { getApiErrorMessage } from '@/client/utils/errors';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import ProfileImage from '@/client/components/common/ProfileImage';
import { userPhotoUrl } from '../UserList/helpers';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import OverviewTab from './tabs/OverviewTab';
import AccessTab from './tabs/AccessTab';
import DangerZoneTab from './tabs/DangerZoneTab';
import PaymentsTab from './tabs/PaymentsTab';
import MessagesTab from './tabs/MessagesTab';
import CollectionsTab from './tabs/CollectionsTab';
import VouchersTab from './tabs/VouchersTab';
import AuditTab from './tabs/AuditTab';
import ListingsTab from './tabs/ListingsTab';
import NotificationFeedTab from './tabs/NotificationFeedTab';
import OrganizationsTab from './tabs/OrganizationsTab';
import { PersonaBadge } from '../UserList/PersonaBadge';

type TabKey =
  | 'overview'
  | 'listings'
  | 'notification-feed'
  | 'organizations'
  | 'access'
  | 'payments'
  | 'messages'
  | 'collections'
  | 'vouchers'
  | 'audit'
  | 'danger';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'listings', label: 'Listings' },
  { key: 'notification-feed', label: 'Notification feed' },
  { key: 'organizations', label: 'Organizations' },
  { key: 'access', label: 'Access' },
  { key: 'payments', label: 'Payments' },
  { key: 'messages', label: 'Messages' },
  { key: 'collections', label: 'Collections' },
  { key: 'vouchers', label: 'Vouchers' },
  { key: 'audit', label: 'Audit logs' },
  { key: 'danger', label: 'Danger zone' },
];

const UserDetailPage = () => {
  const { uid: uidParam } = useParams<{ uid: string }>();
  // Route param is a string; anything non-numeric can't be a uid, so don't
  // fetch — `/api/admin/users/<uid>` is int-routed and would 404 anyway.
  const parsedUid = uidParam ? Number(uidParam) : NaN;
  const uid = Number.isFinite(parsedUid) ? parsedUid : null;
  const navigate = useNavigate();

  useBreadcrumbs([
    { label: 'Users Management', href: '#' },
    { label: 'Users', href: '/admin/users' },
    { label: uid ? `User ${uid}` : 'User' },
  ]);

  // Fetched straight from `GET /api/admin/users/<uid>`, so a deep link to any
  // uid resolves — including held and soft-deleted accounts.
  const detailQuery = useAdminUserDetailQuery(uid);
  const row = detailQuery.data;
  const user = row?.user ?? null;
  const authid = user?.authid ?? null;

  // Tab is deep-linkable via ?tab= so other surfaces can jump straight in.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const tab: TabKey = TABS.some(t => t.key === tabFromUrl) ? (tabFromUrl as TabKey) : 'overview';
  const setTab = (next: TabKey) => {
    setSearchParams(
      prev => {
        const p = new URLSearchParams(prev);
        if (next === 'overview') p.delete('tab');
        else p.set('tab', next);
        return p;
      },
      { replace: true }
    );
  };

  if (uid !== null && detailQuery.isLoading) {
    return (
      <div className="min-h-screen space-y-4 bg-gray-50 p-6">
        <Skeleton className="h-12 w-72" />
        <Skeleton className="h-10 w-full max-w-2xl" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // Fetch failed for a reason other than "no such user" — retryable, so say so
  // rather than claiming the user doesn't exist.
  if (detailQuery.isFetchError) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-2xl rounded-lg border border-red-200 bg-red-50 p-8 text-center">
          <TriangleAlert className="mx-auto h-8 w-8 text-red-600" />
          <h1 className="mt-4 text-lg font-semibold text-gray-900">
            Couldn&apos;t load this user
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {getApiErrorMessage(
              detailQuery.error,
              'The request to load user details failed.'
            )}
          </p>
          <div className="mt-6 flex justify-center gap-2">
            <Button onClick={() => detailQuery.refetch()} disabled={detailQuery.isFetching}>
              <RefreshCw
                className={`mr-2 h-4 w-4 ${detailQuery.isFetching ? 'animate-spin' : ''}`}
              />
              Try again
            </Button>
            <Button variant="outline" asChild>
              <Link to="/admin/users">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to users
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-2xl rounded-lg border border-amber-200 bg-amber-50 p-8 text-center">
          <ShieldAlert className="mx-auto h-8 w-8 text-amber-600" />
          <h1 className="mt-4 text-lg font-semibold text-gray-900">User not found</h1>
          <p className="mt-2 text-sm text-gray-700">
            {uid === null ? (
              <>
                <span className="font-medium">{uidParam}</span> is not a valid user id.
              </>
            ) : (
              <>
                No user exists with uid <span className="font-medium">{uid}</span>. It may
                have been erased, or the link may be wrong.
              </>
            )}
          </p>
          <Button asChild className="mt-6">
            <Link to="/admin/users">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to users
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const fullName =
    [user.first_name, user.last_name].filter(Boolean).join(' ') || 'Unnamed user';
  const isSuspended = user.admin_hold === 1;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 flex-shrink-0">
              <ProfileImage name={fullName} src={userPhotoUrl(user.photo)} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{fullName}</h1>
              <p className="mt-1 text-sm text-gray-500">
                uid {user.uid} · {user.email1 ?? user.ffid ?? user.uuid}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                user.active === 1 ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
              }`}
            >
              {user.active === 1 ? 'Active' : 'Inactive'}
            </span>
            {user.persona === 1 && <PersonaBadge />}
            {isSuspended && (
              <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800">
                Suspended · cannot transact
              </span>
            )}
            <Button variant="outline" asChild>
              <Link to="/admin/users">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Link>
            </Button>
          </div>
        </div>

        {/* Plain-state tabs — there is no Tabs primitive in this project. */}
        <div className="overflow-x-auto border-b border-gray-200">
          <nav className="flex min-w-max gap-6" role="tablist">
            {TABS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={`-mb-px whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                  tab === key
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>

        {tab === 'overview' && <OverviewTab user={user} authid={authid} />}
        {tab === 'listings' && <ListingsTab uid={user.uid} />}
        {tab === 'notification-feed' && <NotificationFeedTab uid={user.uid} />}
        {tab === 'organizations' && <OrganizationsTab uid={user.uid} />}
        {tab === 'access' && <AccessTab row={row!} authid={authid} />}
        {tab === 'payments' && <PaymentsTab uid={user.uid} />}
        {tab === 'messages' && <MessagesTab uid={user.uid} />}
        {tab === 'collections' && <CollectionsTab uid={user.uid} />}
        {tab === 'vouchers' && <VouchersTab uid={user.uid} />}
        {tab === 'audit' && <AuditTab uid={user.uid} authid={authid} />}
        {tab === 'danger' && (
          <DangerZoneTab user={user} onErased={() => navigate('/admin/users')} />
        )}
      </div>
    </div>
  );
};

export default UserDetailPage;
